import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Power, X, Play, Mail, Phone, Send, ChevronsUpDown, Timer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { format } from 'date-fns';
import { AVAILABLE_TRIGGERS, Trigger, TriggerSettings, TEST_TRIGGER, SCHEDULED_TRIGGER, APPOINTMENT_REMINDER, APPOINTMENT_CANCELED, BOOKING_STATUS_TRIGGER } from '../lib/automations/triggers';
import { AVAILABLE_ACTIONS, Action, ActionSettings, SMS_ACTION, EMAIL_ACTION, NOTIFY_PERSON_ACTION, WAIT_ACTION } from '../lib/automations/actions';
import ResponsiveWorkflow from '../components/automations/ResponsiveWorkflow';
import { processAllReminders } from '../services/reminderProcessor';
import { testSpecificReminder } from '../services/testTrigger';

interface Automation {
  id: string;
  title: string;
  is_active: boolean;
  business_id: string;
  triggers?: Trigger[];
  actions?: Action[];
}

export default function Automations() {
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [selectedAutomationId, setSelectedAutomationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState<Trigger | null>(null);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [showTriggerSettings, setShowTriggerSettings] = useState(false);
  const [showActionSettings, setShowActionSettings] = useState(false);
  const [triggerSettings, setTriggerSettings] = useState<TriggerSettings>({});
  const [actionSettings, setActionSettings] = useState<ActionSettings>({});
  const { selectedBusiness } = useBusiness();
  const [showVariablesPopover, setShowVariablesPopover] = useState(false);
  const variablesButtonRef = useRef<HTMLButtonElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    console.log('BusinessContext changed:', { 
      selectedBusiness: selectedBusiness ? {
        id: selectedBusiness.id,
        name: selectedBusiness.name
      } : null
    });
    
    if (selectedBusiness?.id) {
      console.log('Will load automations for business:', selectedBusiness.id);
      loadAutomations();
    } else {
      console.log('No business selected, skipping automations load');
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (selectedTrigger) {
      setTriggerSettings(selectedTrigger.settings || {});
    }
  }, [selectedTrigger]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadAutomations = async () => {
    if (!supabase || !selectedBusiness?.id) {
      console.log('Skipping loadAutomations - no supabase or business ID', { supabase: !!supabase, businessId: selectedBusiness?.id });
      setIsLoading(false);
      return;
    }

    try {
      console.log('Loading automations for business:', selectedBusiness.id);
      const { data, error } = await supabase
        .from('automation_settings')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching automations:', error);
        throw error;
      }

      console.log('Loaded automations data:', data);

      // Process data to include triggers and actions
      const processedData = (data || []).map(automation => {
        const triggers: Trigger[] = [];
        const actionsWithOrder: Array<{action: Action, order: number}> = [];

        // Check for test trigger
        if (automation.test_trigger_active) {
          console.log('Found active test trigger in automation:', automation.id);
          const testTrigger: Trigger = {
            ...TEST_TRIGGER,
            settings: {
              phoneNumber: automation.test_trigger_phone || '',
              email: automation.test_trigger_email || ''
            }
          };
          triggers.push(testTrigger);
        }

        // Check for scheduled trigger
        if (automation.scheduled_trigger_active) {
          console.log('Found active scheduled trigger in automation:', automation.id);
          triggers.push(SCHEDULED_TRIGGER);
        }
        
        // Check for appointment reminder trigger
        if (automation.appointment_reminder_active) {
          console.log('Found active appointment reminder trigger in automation:', automation.id);
          const appointmentReminderTrigger: Trigger = {
            ...APPOINTMENT_REMINDER,
            settings: {
              ...APPOINTMENT_REMINDER.settings,
              reminderHours: automation.appointment_reminder_hours || [24]
            }
          };
          triggers.push(appointmentReminderTrigger);
        }

        // Check for appointment canceled trigger
        if (automation.appointment_canceled_active) {
          console.log('Found active appointment canceled trigger in automation:', automation.id);
          triggers.push(APPOINTMENT_CANCELED);
        }

        // Check for booking status trigger
        if (automation.booking_status_active) {
          console.log('Found active booking status trigger in automation:', automation.id);
          const bookingStatusTrigger: Trigger = {
            ...BOOKING_STATUS_TRIGGER,
            settings: {
              ...BOOKING_STATUS_TRIGGER.settings,
              statusValues: automation.booking_status_trigger_statuses || ['in_progress']
            }
          };
          triggers.push(bookingStatusTrigger);
        }

        // Collect actions with their order information
        if (automation.sms_action_active) {
          console.log('Found active SMS action in automation:', automation.id, 'order:', automation.sms_action_order);
          const smsAction: Action = {
            ...SMS_ACTION,
            settings: {
              ...SMS_ACTION.settings,
              message: automation.sms_action_message || ''
            }
          };
          actionsWithOrder.push({ 
            action: smsAction, 
            order: automation.sms_action_order || 999 // Default high order if no order set
          });
        }
        
        if (automation.email_action_active) {
          console.log('Found active Email action in automation:', automation.id, 'order:', automation.email_action_order);
          const emailAction: Action = {
            ...EMAIL_ACTION,
            settings: {
              ...EMAIL_ACTION.settings,
              subject: automation.email_action_subject || '',
              emailBody: automation.email_action_body || ''
            }
          };
          actionsWithOrder.push({ 
            action: emailAction, 
            order: automation.email_action_order || 999 
          });
        }

        if (automation.notify_person_active) {
          console.log('Found active Notify Person action in automation:', automation.id, 'order:', automation.notify_person_action_order);
          const notifyPersonAction: Action = {
            ...NOTIFY_PERSON_ACTION,
            settings: {
              ...NOTIFY_PERSON_ACTION.settings,
              notifyTarget: automation.notify_person_target || 'custom',
              notifyPhoneNumber: automation.notify_person_phone || '',
              notifyMessage: automation.notify_person_message || 'New notification: [[status_formatted]] for booking #[[booking_number]]'
            }
          };
          actionsWithOrder.push({ 
            action: notifyPersonAction, 
            order: automation.notify_person_action_order || 999 
          });
        }

        if (automation.wait_action_active) {
          console.log('Found active Wait action in automation:', automation.id, 'order:', automation.wait_action_order);
          const waitAction: Action = {
            ...WAIT_ACTION,
            settings: {
              ...WAIT_ACTION.settings,
              waitDuration: automation.wait_action_duration || 30,
              waitUnit: automation.wait_action_unit || 'minutes'
            }
          };
          actionsWithOrder.push({ 
            action: waitAction, 
            order: automation.wait_action_order || 999 
          });
        }

        // Sort actions by their order and extract just the actions
        const actions = actionsWithOrder
          .sort((a, b) => a.order - b.order)
          .map(item => item.action);

        console.log('Actions for automation', automation.id, 'in order:', actions.map(a => `${a.name} (order: ${actionsWithOrder.find(ao => ao.action.id === a.id)?.order})`));

        return {
          ...automation,
          triggers,
          actions
        };
      });

      console.log('Processed automations:', processedData);
      setAutomations(processedData);
    } catch (error) {
      console.error('Error loading automations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!supabase || !selectedBusiness?.id) return;

    try {
      // Create object with only database fields
      const newAutomationForDb = {
        business_id: selectedBusiness.id,
        title: 'New Automation',
        is_active: false,
        test_trigger_active: false,
        test_trigger_phone: '',
        test_trigger_email: '',
        sms_action_active: false,
        sms_action_message: '',
        email_action_active: false,
        email_action_subject: '',
        email_action_body: ''
      };

      // Insert into database
      const { data, error } = await supabase
        .from('automation_settings')
        .insert([newAutomationForDb])
        .select()
        .single();

      if (error) {
        console.error('Error creating automation:', error);
        throw error;
      }
      
      if (data) {
        console.log('Successfully created automation:', data);
        
        // Add the in-memory properties for UI
        const newAutomationWithArrays = {
          ...data,
          triggers: [],
          actions: []
        };
        
        setAutomations([newAutomationWithArrays, ...automations]);
        setSelectedAutomationId(data.id);
        setIsCreating(true);
        setTitle(data.title);
      }
    } catch (error) {
      console.error('Error creating automation:', error);
    }
  };

  const handleTitleChange = async (newTitle: string) => {
    if (!supabase || !selectedAutomationId) return;

    setTitle(newTitle);
    try {
      const { error } = await supabase
        .from('automation_settings')
        .update({ title: newTitle })
        .eq('id', selectedAutomationId);

      if (error) throw error;

      setAutomations(automations.map(automation => 
        automation.id === selectedAutomationId 
          ? { ...automation, title: newTitle }
          : automation
      ));
    } catch (error) {
      console.error('Error updating automation title:', error);
    }
  };

  const handleAutomationSelect = (automation: Automation) => {
    setSelectedAutomationId(automation.id);
    setIsCreating(true);
    setTitle(automation.title);
  };

  const handleDelete = async () => {
    if (!supabase || !selectedAutomationId) return;

    try {
      const { error } = await supabase
        .from('automation_settings')
        .delete()
        .eq('id', selectedAutomationId);

      if (error) throw error;

      setAutomations(automations.filter(a => a.id !== selectedAutomationId));
      setSelectedAutomationId(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error deleting automation:', error);
    }
  };

  const handleToggleActive = async () => {
    if (!supabase || !selectedAutomationId) return;

    const automation = automations.find(a => a.id === selectedAutomationId);
    if (!automation) return;

    const newActiveState = !automation.is_active;

    try {
      const { error } = await supabase
        .from('automation_settings')
        .update({ is_active: newActiveState })
        .eq('id', selectedAutomationId);

      if (error) throw error;

      setAutomations(automations.map(a => 
        a.id === selectedAutomationId 
          ? { ...a, is_active: newActiveState }
          : a
      ));
    } catch (error) {
      console.error('Error toggling automation status:', error);
    }
  };

  const handleTriggerSettingsClick = (trigger: Trigger) => {
    if (!supabase || !selectedAutomationId) return;
    
    setSelectedTrigger(trigger);
    
    // Default settings from the trigger definition
    let initialSettings = trigger.settings || {};
    
    // For trigger-specific settings, we need to load from the database
    if (trigger.id === 'appointment_reminder' || trigger.id === 'booking_status') {
      // Load the settings from database
      supabase
        .from('automation_settings')
        .select('appointment_reminder_hours, booking_status_trigger_statuses')
        .eq('id', selectedAutomationId)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error('Error loading trigger settings:', error);
            return;
          }
          
          if (data) {
            const dbSettings: TriggerSettings = {};
            
            // Set reminder hours if this is a reminder trigger
            if (trigger.id === 'appointment_reminder' && data.appointment_reminder_hours) {
              dbSettings.reminderHours = data.appointment_reminder_hours;
            }
            
            // Set status values if this is a status trigger
            if (trigger.id === 'booking_status' && data.booking_status_trigger_statuses) {
              dbSettings.statusValues = data.booking_status_trigger_statuses;
            }
            
            // Merge with default settings
            setTriggerSettings({ ...initialSettings, ...dbSettings });
          }
        });
    } else {
      // Use the default settings directly
      setTriggerSettings(initialSettings);
    }
    
    setShowTriggerSettings(true);
  };

  const handleOpenTriggerModal = () => {
    if (automations.find(a => a.id === selectedAutomationId)?.triggers?.length) {
      setShowActionModal(true);
    } else {
      setShowTriggerModal(true);
    }
  };

  const handleTestTrigger = async (trigger: Trigger) => {
    try {
      // Execute the trigger and get the returned settings
      const triggerResult = await trigger.execute();
      
      // If the trigger returns settings, use those, otherwise use the defaults
      const triggerSettings = triggerResult && typeof triggerResult === 'object' 
        ? triggerResult as TriggerSettings
        : trigger.settings;
        
      console.log(`Trigger ${trigger.name} executed successfully`, triggerSettings);
      
      // Find the current automation
      const currentAutomation = automations.find(a => a.id === selectedAutomationId);
      if (!currentAutomation || !currentAutomation.actions || currentAutomation.actions.length === 0) {
        return;
      }
      
      // Execute all actions in sequence with the trigger settings
      for (const action of currentAutomation.actions) {
        await handleTestAction(action, triggerSettings);
      }
    } catch (error) {
      console.error('Error executing trigger:', error);
    }
  };

  const handleTestAction = async (action: Action, triggerSettings?: TriggerSettings) => {
    try {
      // Process the action with the trigger settings
      if (action.id === 'sms_action') {
        // Get the message template
        let messageTemplate = action.settings?.message || '';
        
        // Replace variables if triggerSettings is provided
        if (triggerSettings) {
          // Replace using the [[variable_name]] format
          messageTemplate = messageTemplate.replace(/\[\[([^\]]+)\]\]/g, (match, variableName) => {
            const key = variableName.trim();
            return (triggerSettings as any)[key] || match;
          });
        }
        
        // Get the current automation
        const currentAutomation = automations.find(a => a.id === selectedAutomationId);
        if (!currentAutomation) {
          throw new Error('Automation not found');
        }
        
        // Create a new action object with the processed message
        const actionWithProcessedMessage = {
          ...action,
          settings: {
            ...action.settings,
            message: messageTemplate
          }
        };
        
        // Log what we're going to do
        console.log(`Executing SMS Action with message: "${messageTemplate}"`);
        console.log('Using trigger settings:', triggerSettings);
        
        // Execute the action with the correct parameters
        await actionWithProcessedMessage.execute.call(
          actionWithProcessedMessage,
          currentAutomation.business_id,
          triggerSettings
        );
      } else if (action.id === 'email_action') {
        // Get the email templates
        let subject = action.settings?.subject || '';
        let emailBody = action.settings?.emailBody || '';
        
        // Replace variables if triggerSettings is provided
        if (triggerSettings) {
          // Replace using the [[variable_name]] format for subject
          subject = subject.replace(/\[\[([^\]]+)\]\]/g, (match, variableName) => {
            const key = variableName.trim();
            return (triggerSettings as any)[key] || match;
          });
          
          // Replace using the [[variable_name]] format for body
          emailBody = emailBody.replace(/\[\[([^\]]+)\]\]/g, (match, variableName) => {
            const key = variableName.trim();
            return (triggerSettings as any)[key] || match;
          });
        }
        
        // Get the current automation
        const currentAutomation = automations.find(a => a.id === selectedAutomationId);
        if (!currentAutomation) {
          throw new Error('Automation not found');
        }
        
        // Create a new action object with the processed templates
        const actionWithProcessedTemplates = {
          ...action,
          settings: {
            ...action.settings,
            subject,
            emailBody
          }
        };
        
        // Log what we're going to do
        console.log(`Executing Email Action with subject: "${subject}" and body: "${emailBody.substring(0, 100)}..."`);
        console.log('Using trigger settings:', triggerSettings);
        
        // Execute the action with the correct parameters
        await actionWithProcessedTemplates.execute.call(
          actionWithProcessedTemplates,
          currentAutomation.business_id,
          triggerSettings
        );
      } else {
        // Execute other action types
        await action.execute(action.id, triggerSettings);
        console.log(`Action ${action.name} executed successfully`);
      }
    } catch (error) {
      console.error(`Error executing action ${action.name}:`, error);
    }
  };

  const handleAddTrigger = async (trigger: Trigger) => {
    if (!supabase || !selectedAutomationId) return;

    const currentAutomation = automations.find(a => a.id === selectedAutomationId);
    if (!currentAutomation) return;

    try {
      // Update the database to set the trigger as active
      const updateData: any = {};
      
      if (trigger.id === 'test_trigger') {
        updateData.test_trigger_active = true;
        updateData.test_trigger_phone = trigger.settings?.phoneNumber || '';
        updateData.test_trigger_email = trigger.settings?.email || '';
      } else if (trigger.id === 'scheduled_trigger' || trigger.id === 'appointment_scheduled') {
        try {
          // First, check if the column exists by trying to select it
          const { data: columnCheck, error: columnCheckError } = await supabase
            .from('automation_settings')
            .select('scheduled_trigger_active')
            .limit(1);
          
          if (columnCheckError && columnCheckError.message.includes("column") && columnCheckError.message.includes("does not exist")) {
            console.log('The scheduled_trigger_active column does not exist yet. Please run the SQL script to add it.');
            alert('The scheduled_trigger_active column does not exist in the database. Please add it first.');
            throw new Error('Missing required database column: scheduled_trigger_active');
          }
          
          // If we get here, the column exists
          updateData.scheduled_trigger_active = true;
        } catch (columnError) {
          console.error('Error checking for scheduled_trigger_active column:', columnError);
          throw columnError;
        }
      } else if (trigger.id === 'appointment_reminder') {
        try {
          // First, check if the column exists by trying to select it
          const { data: columnCheck, error: columnCheckError } = await supabase
            .from('automation_settings')
            .select('appointment_reminder_active')
            .limit(1);
          
          if (columnCheckError && columnCheckError.message.includes("column") && columnCheckError.message.includes("does not exist")) {
            console.log('The appointment_reminder_active column does not exist yet. Please run the SQL script to add it.');
            alert('The appointment_reminder_active column does not exist in the database. Please add it first.');
            throw new Error('Missing required database column: appointment_reminder_active');
          }
          
          // If we get here, the column exists
          updateData.appointment_reminder_active = true;
          updateData.appointment_reminder_hours = trigger.settings?.reminderHours || [24];
        } catch (columnError) {
          console.error('Error checking for appointment_reminder_active column:', columnError);
          throw columnError;
        }
      } else if (trigger.id === 'appointment_canceled') {
        try {
          // First, check if the column exists by trying to select it
          const { data: columnCheck, error: columnCheckError } = await supabase
            .from('automation_settings')
            .select('appointment_canceled_active')
            .limit(1);
          
          if (columnCheckError && columnCheckError.message.includes("column") && columnCheckError.message.includes("does not exist")) {
            console.log('The appointment_canceled_active column does not exist yet. Please run the SQL script to add it.');
            alert('The appointment_canceled_active column does not exist in the database. Please add it first.');
            throw new Error('Missing required database column: appointment_canceled_active');
          }
          
          // If we get here, the column exists
          updateData.appointment_canceled_active = true;
        } catch (columnError) {
          console.error('Error checking for appointment_canceled_active column:', columnError);
          throw columnError;
        }
      } else if (trigger.id === 'booking_status') {
        try {
          // First, check if the column exists by trying to select it
          const { data: columnCheck, error: columnCheckError } = await supabase
            .from('automation_settings')
            .select('booking_status_active')
            .limit(1);
          
          if (columnCheckError && columnCheckError.message.includes("column") && columnCheckError.message.includes("does not exist")) {
            console.log('The booking_status_active column does not exist yet. Please run the SQL script to add it.');
            alert('The booking_status_active column does not exist in the database. Please add it first.');
            throw new Error('Missing required database column: booking_status_active');
          }
          
          // If we get here, the column exists
          updateData.booking_status_active = true;
          // Set default status to in_progress
          updateData.booking_status_trigger_statuses = ['in_progress'];
        } catch (columnError) {
          console.error('Error checking for booking_status_active column:', columnError);
          throw columnError;
        }
      }
      
      const { error } = await supabase
        .from('automation_settings')
        .update(updateData)
        .eq('id', selectedAutomationId);
        
      if (error) throw error;

      // Update local state
      const updatedAutomation = {
        ...currentAutomation,
        triggers: [...(currentAutomation.triggers || []), trigger]
      };

      setAutomations(automations.map(a => 
        a.id === selectedAutomationId ? updatedAutomation : a
      ));
      setShowTriggerModal(false);
    } catch (error) {
      console.error('Error saving trigger to database:', error);
    }
  };

  const handleRemoveTrigger = async (triggerId: string) => {
    if (!supabase || !selectedAutomationId) return;

    const currentAutomation = automations.find(a => a.id === selectedAutomationId);
    if (!currentAutomation) return;

    try {
      // Update the database to set the trigger as inactive
      const updateData: any = {};
      
      if (triggerId === 'test_trigger') {
        updateData.test_trigger_active = false;
      } else if (triggerId === 'scheduled_trigger' || triggerId === 'appointment_scheduled') {
        updateData.scheduled_trigger_active = false;
      } else if (triggerId === 'appointment_reminder') {
        updateData.appointment_reminder_active = false;
      } else if (triggerId === 'appointment_canceled') {
        updateData.appointment_canceled_active = false;
      } else if (triggerId === 'booking_status') {
        updateData.booking_status_active = false;
        updateData.booking_status_trigger_statuses = [];
      }
      
      const { error } = await supabase
        .from('automation_settings')
        .update(updateData)
        .eq('id', selectedAutomationId);
        
      if (error) throw error;

      // Update local state
      const updatedAutomation = {
        ...currentAutomation,
        triggers: (currentAutomation.triggers || []).filter(t => t.id !== triggerId)
      };

      setAutomations(automations.map(a => 
        a.id === selectedAutomationId ? updatedAutomation : a
      ));
    } catch (error) {
      console.error('Error removing trigger from database:', error);
    }
  };

  const handleUpdateTriggerSettings = async (settings: TriggerSettings) => {
    if (!supabase || !selectedTrigger || !selectedAutomationId) return;

    try {
      // Create update data based on the trigger type and settings
      const updateData: any = {};
      
      if (selectedTrigger.id === 'appointment_reminder') {
        // Update reminder hours, ensuring it's stored as an array
        updateData.appointment_reminder_hours = settings.reminderHours || [24]; // Default to 24 if not set
      }
      
      if (selectedTrigger.id === 'booking_status') {
        // Update the booking status trigger statuses
        updateData.booking_status_trigger_statuses = settings.statusValues || [];
      }
      
      console.log('Updating trigger settings in database:', updateData);
      
      // Update the database setting
      const { error } = await supabase
        .from('automation_settings')
        .update(updateData)
        .eq('id', selectedAutomationId);
        
      if (error) {
        console.error('Error updating trigger settings in database:', error);
        throw error;
      }
      
      console.log('Trigger settings updated in database successfully');
      
      // Update local state - update the in-memory trigger object
      const currentAutomation = automations.find(a => a.id === selectedAutomationId);
      if (!currentAutomation) return;
      
      const updatedTriggers = currentAutomation.triggers?.map(t => 
        t.id === selectedTrigger.id ? { ...t, settings } : t
      ) || [];
      
      setAutomations(automations.map(a => 
        a.id === selectedAutomationId ? { ...a, triggers: updatedTriggers } : a
      ));
      
      setShowTriggerSettings(false);
    } catch (error) {
      console.error('Error updating trigger settings:', error);
    }
  };

  const handleTriggerSettingChange = (key: keyof TriggerSettings, value: string | number[] | string[]) => {
    setTriggerSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleOpenActionModal = () => {
    setShowActionModal(true);
  };

  const handleAddAction = async (action: Action) => {
    const currentAutomation = automations.find(a => a.id === selectedAutomationId);
    if (!currentAutomation || !supabase) return;

    // Create a deep copy of the action to avoid prototype issues
    const actionCopy = {
      ...action,
      settings: { ...action.settings } // Deep copy settings
    };

    try {
      // Calculate the next order number for this action
      // Find the highest existing order number and add 1
      let nextOrder = 1;
      
      // Get current automation data to check existing action orders
      const { data: currentData, error: fetchError } = await supabase
        .from('automation_settings')
        .select('sms_action_order, email_action_order, notify_person_action_order, wait_action_order')
        .eq('id', selectedAutomationId)
        .single();
        
      if (fetchError) {
        console.error('Error fetching current action orders:', fetchError);
      } else if (currentData) {
        // Find the highest existing order number
        const existingOrders = [
          currentData.sms_action_order,
          currentData.email_action_order,
          currentData.notify_person_action_order,
          currentData.wait_action_order
        ].filter(order => order !== null && order !== undefined);
        
        if (existingOrders.length > 0) {
          nextOrder = Math.max(...existingOrders) + 1;
        }
      }
      
      console.log(`Assigning order ${nextOrder} to new ${action.name} action`);

      // Only update database fields - don't send the actions array
      const updateData: any = {};
      
      if (action.id === 'sms_action') {
        updateData.sms_action_active = true;
        updateData.sms_action_message = action.settings?.message || '';
        updateData.sms_action_order = nextOrder;
      } else if (action.id === 'email_action') {
        updateData.email_action_active = true;
        updateData.email_action_subject = action.settings?.subject || '';
        updateData.email_action_body = action.settings?.emailBody || '';
        updateData.email_action_order = nextOrder;
      } else if (action.id === 'notify_person_action') {
        updateData.notify_person_active = true;
        updateData.notify_person_target = action.settings?.notifyTarget || 'custom';
        updateData.notify_person_phone = action.settings?.notifyPhoneNumber || '';
        updateData.notify_person_message = action.settings?.notifyMessage || 'New notification: [[status_formatted]] for booking #[[booking_number]]';
        updateData.notify_person_action_order = nextOrder;
      } else if (action.id === 'wait_action') {
        updateData.wait_action_active = true;
        updateData.wait_action_duration = action.settings?.waitDuration || 30;
        updateData.wait_action_unit = action.settings?.waitUnit || 'minutes';
        updateData.wait_action_order = nextOrder;
      }
      
      console.log('Updating database with action and order:', updateData);
      
      // Update the database
      const { error } = await supabase
        .from('automation_settings')
        .update(updateData)
        .eq('id', selectedAutomationId);
        
      if (error) {
        console.error('Error updating action in database:', error);
        throw error;
      }
      
      console.log('Database updated successfully with action order');
      
      // Update local state with the in-memory action object
      const updatedAutomation = {
        ...currentAutomation,
        actions: [...(currentAutomation.actions || []), actionCopy]
      };

      setAutomations(automations.map(a => 
        a.id === selectedAutomationId ? updatedAutomation : a
      ));
      
      setShowActionModal(false);
    } catch (error) {
      console.error('Error adding action:', error);
    }
  };

  const handleActionSettingsClick = (action: Action) => {
    setSelectedAction(action);
    setActionSettings(action.settings || {});
    setShowActionSettings(true);
  };

  const handleActionSettingChange = (key: keyof ActionSettings, value: string) => {
    setActionSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleUpdateActionSettings = async (settings: ActionSettings) => {
    if (!selectedAction || !selectedAutomationId || !supabase) return;

    // Only update database fields
    const updateData: any = {};
    
    if (selectedAction.id === 'sms_action') {
      updateData.sms_action_message = settings.message || '';
    } else if (selectedAction.id === 'email_action') {
      updateData.email_action_subject = settings.subject || '';
      updateData.email_action_body = settings.emailBody || '';
    } else if (selectedAction.id === 'notify_person_action') {
      updateData.notify_person_target = settings.notifyTarget || 'custom';
      updateData.notify_person_phone = settings.notifyPhoneNumber || '';
      updateData.notify_person_message = settings.notifyMessage || 'New notification: [[status_formatted]] for booking #[[booking_number]]';
    } else if (selectedAction.id === 'wait_action') {
      updateData.wait_action_duration = settings.waitDuration || 30;
      updateData.wait_action_unit = settings.waitUnit || 'minutes';
    }
    
    console.log('Updating action settings in database:', updateData);
    
    // Update the database - only sending database fields
    const { error } = await supabase
      .from('automation_settings')
      .update(updateData)
      .eq('id', selectedAutomationId);
      
    if (error) {
      console.error('Error updating action settings in database:', error);
      throw error;
    }
    
    console.log('Action settings updated in database successfully');
    
    // Update local state - updating the in-memory action object
    const currentAutomation = automations.find(a => a.id === selectedAutomationId);
    if (!currentAutomation) return;
    
    const updatedActions = currentAutomation.actions?.map(a => 
      a.id === selectedAction.id ? { ...a, settings } : a
    ) || [];
    
    setAutomations(automations.map(a => 
      a.id === selectedAutomationId ? { ...a, actions: updatedActions } : a
    ));
    
    // Also update the selected action
    setSelectedAction({ ...selectedAction, settings });
    
    setShowActionSettings(false);
  };

  const handleRemoveAction = async (actionId: string) => {
    if (!supabase || !selectedAutomationId) return;

    try {
      // Create update data based on the action type
      const updateData: any = {};
      
      if (actionId === 'sms_action') {
        updateData.sms_action_active = false;
        updateData.sms_action_message = '';
        updateData.sms_action_order = null; // Clear the order
      } else if (actionId === 'email_action') {
        updateData.email_action_active = false;
        updateData.email_action_subject = '';
        updateData.email_action_body = '';
        updateData.email_action_order = null; // Clear the order
      } else if (actionId === 'notify_person_action') {
        updateData.notify_person_active = false;
        updateData.notify_person_action_order = null; // Clear the order
      } else if (actionId === 'wait_action') {
        updateData.wait_action_active = false;
        updateData.wait_action_duration = 30;
        updateData.wait_action_unit = 'minutes';
        updateData.wait_action_order = null; // Clear the order
      }
      
      console.log('Removing action and clearing order:', actionId, updateData);
      
      const { error } = await supabase
        .from('automation_settings')
        .update(updateData)
        .eq('id', selectedAutomationId);

      if (error) throw error;

      // Update local state
      setAutomations(automations.map(a => 
        a.id === selectedAutomationId ? {
          ...a,
          actions: a.actions?.filter(a => a.id !== actionId)
        } : a
      ));
      
      console.log('Action removed successfully and order cleared');
    } catch (error) {
      console.error('Error removing action from database:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-3rem)] flex flex-col md:flex-row relative overflow-hidden">
      {/* Left Sidebar - full width on mobile, fixed width on desktop */}
      <div className={`${isMobile ? 'w-full' : 'w-64'} bg-white border-b md:border-b-0 md:border-r border-gray-200 md:min-h-full md:shrink-0 ${isCreating && isMobile ? 'hidden' : 'block'}`}>
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-900">Automations</h2>
            <button
              className="inline-flex items-center px-2 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleCreate}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Create
            </button>
          </div>
          <div className="space-y-1 overflow-y-auto max-h-[calc(100vh-8rem)]">
            {automations.map(automation => (
              <button
                key={automation.id}
                onClick={() => handleAutomationSelect(automation)}
                className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors flex items-center justify-between ${
                  selectedAutomationId === automation.id
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{automation.title}</span>
                {automation.is_active && (
                  <div className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0 ml-2" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content - full width */}
      <div className={`flex-1 overflow-auto p-4 md:p-6 ${isCreating || !isMobile ? 'block' : 'hidden'}`}>
        {isCreating ? (
          <div className="space-y-6">
            {/* Mobile back button - moved to top */}
            {isMobile && (
              <button
                onClick={() => {
                  setSelectedAutomationId(null);
                  setIsCreating(false);
                }}
                className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to automations
              </button>
            )}
            
            {/* Title Section with Delete Button */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 flex-1 min-w-0">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="text-xl sm:text-2xl font-bold bg-transparent border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none px-1 py-1 w-full"
                  placeholder="Automation Title"
                />
                <button
                  onClick={handleToggleActive}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center space-x-1.5 transition-colors whitespace-nowrap ${
                    automations.find(a => a.id === selectedAutomationId)?.is_active
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Power className="h-4 w-4" />
                  <span>
                    {automations.find(a => a.id === selectedAutomationId)?.is_active
                      ? 'Active'
                      : 'Inactive'}
                  </span>
                </button>
              </div>
              <button
                onClick={handleDelete}
                className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                title="Delete automation"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>

            {/* Use ResponsiveWorkflow component */}
            <ResponsiveWorkflow
              triggers={automations.find(a => a.id === selectedAutomationId)?.triggers || []}
              actions={automations.find(a => a.id === selectedAutomationId)?.actions || []}
              onTriggerSettingsClick={handleTriggerSettingsClick}
              onActionSettingsClick={handleActionSettingsClick}
              onTestTrigger={handleTestTrigger}
              onRemoveTrigger={handleRemoveTrigger}
              onRemoveAction={handleRemoveAction}
              onAddClick={handleOpenTriggerModal}
            />
          </div>
        ) : (
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Automations</h1>
          </div>
        )}
      </div>

      {/* Right Side Trigger Modal - make it full screen on mobile */}
      {showTriggerModal && (
        <div className={`fixed ${isMobile ? 'inset-0' : 'inset-y-0 right-0 w-96'} bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-200 ease-in-out z-30 overflow-auto`}>
          <div className="h-full flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-yellow-900">Available Triggers</h2>
              <button
                onClick={() => setShowTriggerModal(false)}
                className="p-1 hover:bg-yellow-50 rounded-full"
              >
                <X className="h-5 w-5 text-yellow-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4 md:p-6">
              <div className="space-y-4">
                {AVAILABLE_TRIGGERS.map((trigger) => (
                  <div key={trigger.id} className="bg-yellow-50 rounded-lg border border-yellow-200 p-4 w-full max-w-md mx-auto hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 flex-shrink-0 rounded-full bg-yellow-400"></div>
                          <h4 className="text-sm font-medium text-yellow-900 truncate">{trigger.name}</h4>
                        </div>
                        <p className="text-sm text-yellow-700 mt-1 ml-4 line-clamp-2">{trigger.description}</p>
                      </div>
                      <button
                        onClick={() => handleAddTrigger(trigger)}
                        className="p-1.5 text-yellow-700 hover:text-yellow-800 hover:bg-yellow-100 rounded-md transition-colors flex-shrink-0 ml-2"
                        title="Add trigger"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right Side Action Modal - make it full screen on mobile */}
      {showActionModal && (
        <div className={`fixed ${isMobile ? 'inset-0' : 'inset-y-0 right-0 w-96'} bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-200 ease-in-out z-30 overflow-auto`}>
          <div className="h-full flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-blue-900">Available Actions</h2>
              <button
                onClick={() => setShowActionModal(false)}
                className="p-1 hover:bg-blue-50 rounded-full"
              >
                <X className="h-5 w-5 text-blue-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4 md:p-6">
              <div className="space-y-4">
                {AVAILABLE_ACTIONS.map((action) => (
                  <div key={action.id} className="bg-blue-50 rounded-lg border border-blue-200 p-4 w-full max-w-md mx-auto hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 flex-shrink-0 rounded-full bg-blue-400"></div>
                          <h4 className="text-sm font-medium text-blue-900 truncate">{action.name}</h4>
                        </div>
                        <p className="text-sm text-blue-700 mt-1 ml-4 line-clamp-2">{action.description}</p>
                      </div>
                      <button
                        onClick={() => handleAddAction(action)}
                        className="p-1.5 text-blue-700 hover:text-blue-800 hover:bg-blue-100 rounded-md transition-colors flex-shrink-0 ml-2"
                        title="Add action"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modals - centered and responsive */}
      {showTriggerSettings && selectedTrigger && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-auto max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-yellow-900">Trigger Settings</h2>
              <button
                onClick={() => setShowTriggerSettings(false)}
                className="p-1 hover:bg-yellow-50 rounded-full"
              >
                <X className="h-5 w-5 text-yellow-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {selectedTrigger.id === 'test_trigger' && (
                <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={triggerSettings.phoneNumber || ''}
                  onChange={(e) => handleTriggerSettingChange('phoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="+1 (555) 555-5555"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={triggerSettings.email || ''}
                  onChange={(e) => handleTriggerSettingChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="example@email.com"
                />
              </div>
                </>
              )}
              
              {selectedTrigger.id === 'appointment_reminder' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Send reminder before appointment
                  </label>
                  <div className="space-y-3">
                    {[1, 2, 24, 48].map((hour) => (
                      <div key={hour} className="flex items-center">
                        <input
                          type="radio"
                          id={`reminder-${hour}`}
                          name="reminderHours"
                          checked={(triggerSettings.reminderHours && triggerSettings.reminderHours[0] === hour) || 
                                  (!triggerSettings.reminderHours && hour === 24)} // Default to 24 if not set
                          onChange={() => {
                            // Just set a single hour in the array
                            handleTriggerSettingChange('reminderHours', [hour]);
                          }}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300"
                        />
                        <label htmlFor={`reminder-${hour}`} className="ml-2 block text-sm text-gray-700">
                          {hour} {hour === 1 ? 'hour' : 'hours'} before
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Reminder will be sent at the selected time before each appointment's drop-off date and time.
                  </p>
                </div>
              )}
              
              {selectedTrigger.id === 'booking_status' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Trigger when booking status changes to:
                  </label>
                  <div className="space-y-3">
                    {['in_progress', 'completed'].map((status) => (
                      <div key={status} className="flex items-center">
                        <input
                          type="radio"
                          id={`status-${status}`}
                          name="booking_status"
                          checked={triggerSettings.statusValues ? 
                            (triggerSettings.statusValues as string[])?.includes(status) : 
                            false}
                          onChange={() => {
                            // Set only this status (single selection)
                            handleTriggerSettingChange('statusValues', [status]);
                          }}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300"
                        />
                        <label htmlFor={`status-${status}`} className="ml-2 block text-sm text-gray-700">
                          {status === 'in_progress' ? 'In Progress' : 'Completed'}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Automation will trigger when a booking's status changes to the selected value.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowTriggerSettings(false)}
                  className="px-4 py-2 mr-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateTriggerSettings(triggerSettings)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Settings Modal - centered and responsive */}
      {showActionSettings && selectedAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-auto max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-blue-900">Action Settings</h2>
              <button
                onClick={() => setShowActionSettings(false)}
                className="p-1 hover:bg-blue-50 rounded-full"
              >
                <X className="h-5 w-5 text-blue-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {selectedAction.id === 'sms_action' && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Message
                    </label>
                    <button
                      ref={variablesButtonRef}
                      onClick={() => setShowVariablesPopover(!showVariablesPopover)} 
                      className="text-xs text-blue-600 flex items-center"
                    >
                      Variables <ChevronsUpDown className="h-3 w-3 ml-1" />
                    </button>
                  </div>
                  <textarea
                    value={actionSettings.message || ''}
                    onChange={(e) => handleActionSettingChange('message', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                    placeholder="Enter your message..."
                  />
                  {showVariablesPopover && (
                    <div className="mt-2 p-2 bg-gray-100 rounded-md text-sm">
                      <p className="font-medium mb-1">Available Variables:</p>
                      <ul className="list-disc list-inside">
                        <li><code className="bg-gray-200 px-1 rounded">[[customer_first_name]]</code> - Customer's first name</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[customer_full_name]]</code> - Customer's full name</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[drop_off_date_formatted]]</code> - Appointment date</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[drop_off_time_formatted]]</code> - Appointment time</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[start_date_formatted]]</code> - Start date</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[start_time_formatted]]</code> - Start time</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[phoneNumber]]</code> - Customer phone</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[email]]</code> - Customer email</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[business_name]]</code> - Business name</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[booking_number]]</code> - Booking number</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[booking_title]]</code> - Booking title</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[service_name]]</code> - Service name</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[vehicle]]</code> - Full vehicle description</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[vehicle_make]]</code> - Vehicle make</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[vehicle_model]]</code> - Vehicle model</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[vehicle_year]]</code> - Vehicle year</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[vehicle_color]]</code> - Vehicle color</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[status]]</code> - Status code (in_progress, completed)</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[status_formatted]]</code> - Status name (In Progress, Completed)</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[status_date]]</code> - Date of status change</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[status_time]]</code> - Time of status change</li>
                        <li><code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> - Customer feedback link (for completed bookings)</li>
                      </ul>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    You can include variables like [[customer_first_name]] that will be replaced with actual values.
                  </p>
                </div>
              )}
              
              {selectedAction.id === 'email_action' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={actionSettings.subject || ''}
                      onChange={(e) => handleActionSettingChange('subject', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Email subject..."
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Email Body
                      </label>
                      <button
                        onClick={() => setShowVariablesPopover(!showVariablesPopover)} 
                        className="text-xs text-blue-600 flex items-center"
                      >
                        Variables <ChevronsUpDown className="h-3 w-3 ml-1" />
                      </button>
                    </div>
                    <textarea
                      value={actionSettings.emailBody || ''}
                      onChange={(e) => handleActionSettingChange('emailBody', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                      placeholder="Email body content..."
                    />
                    {showVariablesPopover && (
                      <div className="mt-2 p-2 bg-gray-100 rounded-md text-sm">
                        <p className="font-medium mb-1">Available Variables:</p>
                        <ul className="list-disc list-inside">
                          <li><code className="bg-gray-200 px-1 rounded">[[customer_first_name]]</code> - Customer's first name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[customer_full_name]]</code> - Customer's full name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[drop_off_date_formatted]]</code> - Appointment date</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[drop_off_time_formatted]]</code> - Appointment time</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[start_date_formatted]]</code> - Start date</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[start_time_formatted]]</code> - Start time</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[phoneNumber]]</code> - Customer phone</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[email]]</code> - Customer email</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[business_name]]</code> - Business name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[booking_number]]</code> - Booking number</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[booking_title]]</code> - Booking title</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[service_name]]</code> - Service name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle]]</code> - Full vehicle description</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_make]]</code> - Vehicle make</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_model]]</code> - Vehicle model</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_year]]</code> - Vehicle year</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_color]]</code> - Vehicle color</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status]]</code> - Status code (in_progress, completed)</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status_formatted]]</code> - Status name (In Progress, Completed)</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status_date]]</code> - Date of status change</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status_time]]</code> - Time of status change</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> - Customer feedback link (for completed bookings)</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    You can include variables like [[customer_first_name]] that will be replaced with actual values.
                  </p>
                </div>
              )}
              
              {selectedAction.id === 'notify_person_action' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Who to notify
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="notify-resources"
                          name="notifyTarget"
                          checked={actionSettings.notifyTarget === 'all_resources'}
                          onChange={() => handleActionSettingChange('notifyTarget', 'all_resources')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label htmlFor="notify-resources" className="ml-2 block text-sm text-gray-700">
                          All Resources on Booking
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="notify-custom"
                          name="notifyTarget"
                          checked={actionSettings.notifyTarget === 'custom'}
                          onChange={() => handleActionSettingChange('notifyTarget', 'custom')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label htmlFor="notify-custom" className="ml-2 block text-sm text-gray-700">
                          Custom Phone Number
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {actionSettings.notifyTarget === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={actionSettings.notifyPhoneNumber || ''}
                        onChange={(e) => handleActionSettingChange('notifyPhoneNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1 (555) 555-5555"
                      />
                    </div>
                  )}
                  
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Notification Message
                      </label>
                      <button
                        onClick={() => setShowVariablesPopover(!showVariablesPopover)} 
                        className="text-xs text-blue-600 flex items-center"
                      >
                        Variables <ChevronsUpDown className="h-3 w-3 ml-1" />
                      </button>
                    </div>
                    <textarea
                      value={actionSettings.notifyMessage || ''}
                      onChange={(e) => handleActionSettingChange('notifyMessage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                      placeholder="Enter your notification message..."
                    />
                    {showVariablesPopover && (
                      <div className="mt-2 p-2 bg-gray-100 rounded-md text-sm">
                        <p className="font-medium mb-1">Available Variables:</p>
                        <ul className="list-disc list-inside">
                          <li><code className="bg-gray-200 px-1 rounded">[[customer_first_name]]</code> - Customer's first name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[customer_full_name]]</code> - Customer's full name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[drop_off_date_formatted]]</code> - Appointment date</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[drop_off_time_formatted]]</code> - Appointment time</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[start_date_formatted]]</code> - Start date</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[start_time_formatted]]</code> - Start time</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[phoneNumber]]</code> - Customer phone</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[email]]</code> - Customer email</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[business_name]]</code> - Business name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[booking_number]]</code> - Booking number</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[booking_title]]</code> - Booking title</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[service_name]]</code> - Service name</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle]]</code> - Full vehicle description</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_make]]</code> - Vehicle make</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_model]]</code> - Vehicle model</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_year]]</code> - Vehicle year</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[vehicle_color]]</code> - Vehicle color</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status]]</code> - Status code (in_progress, completed)</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status_formatted]]</code> - Status name (In Progress, Completed)</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status_date]]</code> - Date of status change</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[status_time]]</code> - Time of status change</li>
                          <li><code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> - Customer feedback link (for completed bookings)</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    This message will be sent via SMS to the selected recipient(s).
                  </p>
                </div>
              )}
              
              {selectedAction.id === 'wait_action' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Wait Duration
                    </label>
                    <div className="flex space-x-3">
                      <div className="flex-1">
                        <input
                          type="number"
                          min="1"
                          value={actionSettings.waitDuration || 30}
                          onChange={(e) => handleActionSettingChange('waitDuration', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="30"
                        />
                      </div>
                      <div className="flex-1">
                        <select
                          value={actionSettings.waitUnit || 'minutes'}
                          onChange={(e) => handleActionSettingChange('waitUnit', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      The automation will pause for this duration before continuing to the next action.
                    </p>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <Timer className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800">Wait Action Info</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          This action will delay the automation sequence for the specified time. 
                          All trigger data will be preserved and passed to the next action after the wait period.
                        </p>
                        <p className="text-xs text-yellow-600 mt-2">
                          <strong>Example:</strong> Wait 30 minutes after booking confirmation before sending a reminder SMS.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowActionSettings(false)}
                  className="px-4 py-2 mr-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateActionSettings(actionSettings)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 